import { GoogleGenerativeAI } from '@google/generative-ai';

const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY || '');

/**
 * Uses Gemini Vision (Flash) to inspect an image for safety violations.
 * Specifically checks for nudity, sexual content, violence, and hate speech.
 */
export const checkImageSafety = async (file: File): Promise<{ safe: boolean; reason?: string }> => {
    const model = genAI.getGenerativeModel({
        model: 'gemini-1.5-flash',
        generationConfig: {
            temperature: 0.1,
            topP: 0.1,
            topK: 1,
            maxOutputTokens: 100,
        }
    });

    try {
        // Convert File to base64 for Gemini
        const reader = new FileReader();
        const imageData = await new Promise<string>((resolve, reject) => {
            reader.onload = () => {
                const res = reader.result;
                if (typeof res === 'string') {
                    const base64 = res.split(',')[1];
                    if (base64) resolve(base64);
                    else reject('Malformed image data');
                } else {
                    reject('Failed to read file as string');
                }
            };
            reader.onerror = () => reject('FileReader error');
            reader.readAsDataURL(file);
        });

        const prompt = `
            Act as an AI Safety Moderator for a high school sports training application.
            Inspect the attached image for safety violations.
            
            **Rules:**
            1. Flag ANY nudity or suggestive sexual content (NSFW).
            2. Flag ANY extreme violence, weapons, or gore.
            3. Flag ANY hate symbols or offensive text.
            4. Flag ANY content that clearly depicts illegal drug use.
            
            **Requirements:**
            Return ONLY a JSON object with this exact structure:
            {
                "safe": boolean,
                "reason": "short explanation if not safe, otherwise empty"
            }
        `;

        const result = await model.generateContent([
            prompt,
            {
                inlineData: {
                    data: imageData,
                    mimeType: file.type
                }
            }
        ]);

        const response = await result.response;
        const text = response.text().replace(/```json/g, '').replace(/```/g, '').trim();

        return JSON.parse(text);
    } catch (error) {
        console.error('Safety Check Error:', error);
        // If the AI fails, we default to safe but log the error 
        // Or we could block by default for maximum security.
        return { safe: true };
    }
};
